import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Eye, Link2, Facebook, Twitter, Linkedin, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import BlogCard from '../components/BlogCard';
import { getPostBySlug, getPosts } from '../services/api';
import config from '../config';

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [popularPosts, setPopularPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const fetchPost = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPostBySlug(slug);
      if (response.success) {
        setPost(response.data);
        // Fetch popular posts (excluding current post)
        fetchPopularPosts(response.data.category_id);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchPopularPosts = useCallback(async (categoryId) => {
    try {
      const response = await getPosts({ 
        limit: 5, 
        category_id: categoryId,
        sort: 'views',
        order: 'DESC'
      });
      if (response.success) {
        // Filter out current post
        const filtered = response.data.filter(p => p.slug !== slug);
        setPopularPosts(filtered.slice(0, 4));
      }
    } catch (error) {
      console.error('Error fetching popular posts:', error);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchPost]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const generateTextImage = (title, categoryColor) => {
    return (
      <div 
        className="w-full h-full flex items-center justify-center"
        style={{ backgroundColor: categoryColor || '#3B82F6' }}
      >
        {/* No text - just solid color background */}
      </div>
    );
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = post.title;
    const text = post.excerpt;
    
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} - ${text} ${url}`)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(title)}`,
      copy: url
    };
    
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    } else if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
          url: url,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else if (shareUrls[platform]) {
      window.location.href = shareUrls[platform];
    }
  };

  if (loading) {
    return (
      <>
        {/* Hero Section Skeleton */}
        <div className="relative h-[60vh] min-h-[400px] bg-gray-900">
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-12">
            <div className="max-w-4xl">
              <div className="h-8 bg-gray-700 rounded-full mb-4 w-32 animate-pulse"></div>
              <div className="h-12 md:h-16 lg:h-20 bg-gray-700 rounded mb-6 w-3/4 animate-pulse"></div>
              <div className="flex space-x-6">
                <div className="h-4 bg-gray-700 rounded w-20 animate-pulse"></div>
                <div className="h-4 bg-gray-700 rounded w-16 animate-pulse"></div>
                <div className="h-4 bg-gray-700 rounded w-16 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Skeleton */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content Skeleton */}
            <div className="flex-1 lg:max-w-6xl">
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 -mt-24 relative z-10">
                {/* Meta Info Skeleton */}
                <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-gray-200">
                  <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-12 animate-pulse"></div>
                </div>

                {/* Excerpt Skeleton */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
                  <div className="space-y-2">
                    <div className="h-4 bg-blue-200 rounded w-full animate-pulse"></div>
                    <div className="h-4 bg-blue-200 rounded w-5/6 animate-pulse"></div>
                    <div className="h-4 bg-blue-200 rounded w-4/5 animate-pulse"></div>
                  </div>
                </div>
                
                {/* Content Skeleton */}
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5 animate-pulse"></div>
                </div>

                {/* Share Section Skeleton */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="mb-6">
                    <div className="h-6 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded w-48 animate-pulse"></div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar Skeleton */}
            <div className="lg:w-80 lg:flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="h-6 bg-gray-200 rounded w-32 mb-6 animate-pulse"></div>
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="flex space-x-3 p-3 rounded-lg">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg animate-pulse"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                        <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
        <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
        <Link to="/blog" className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition">
          <ArrowLeft size={20} />
          <span>Back to Blog</span>
        </Link>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .prose * {
          word-break: break-word !important;
          overflow-wrap: anywhere !important;
          hyphens: auto !important;
        }
        .prose img {
          max-width: 100% !important;
          height: auto !important;
        }
        .prose pre {
          overflow-x: auto !important;
          white-space: pre-wrap !important;
          word-break: break-all !important;
        }
        .prose table {
          table-layout: fixed !important;
          width: 100% !important;
          word-break: break-word !important;
        }
        .prose td, .prose th {
          word-break: break-word !important;
          max-width: 100% !important;
        }
      `}</style>
      <SEO 
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        keywords={post.meta_keywords || `${post.title}, ${post.category_name}, blog, article, ${post.tags}`}
        image={post.featured_image}
        type="article"
        author={post.author_name}
        publishDate={post.created_at}
        modifiedDate={post.updated_at || post.created_at}
        category={post.category_name}
        tags={post.tags}
        readingTime={post.reading_time}
        url={`${config.SITE_URL}/blog/${post.slug}`}
      />

      <article className="animate-fade-in">
        <div className="relative h-[60vh] min-h-[400px] bg-gray-900">
          {!imageError ? (
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover opacity-80"
              onError={handleImageError}
            />
          ) : (
            generateTextImage(post.title, post.category_color)
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-12">
            <Link
              to="/blog"
              className="inline-flex items-center space-x-2 text-white/80 hover:text-white mb-6 transition"
            >
              <ArrowLeft size={20} />
              <span>Back to Blog</span>
            </Link>
            
            <div className="max-w-4xl">
              <Link
                to={`/category/${post.category_slug}`}
                className="inline-block px-4 py-2 rounded-full text-sm font-semibold text-white mb-4"
                style={{ backgroundColor: post.category_color }}
              >
                {post.category_name}
              </Link>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-slide-up">
                {post.title}
              </h1>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content - Left Side */}
            <div className="flex-1 lg:max-w-6xl">
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 -mt-24 relative z-10 animate-slide-up">
                
                {/* Post Meta Info */}
                <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-gray-200">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Calendar size={16} />
                    <span className="text-sm">{formatDate(post.created_at)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Clock size={16} />
                    <span className="text-sm">{post.reading_time || 5} min read</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Eye size={16} />
                    <span className="text-sm">{post.views || 0} views</span>
                  </div>
                </div>

                {/* Excerpt */}
                {post.excerpt && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
                    <p className="text-lg text-gray-800 leading-relaxed italic">
                      {post.excerpt}
                    </p>
                  </div>
                )}
                
                {/* Content */}
                <div className="prose prose-lg max-w-none break-words overflow-wrap-anywhere">
                  <div 
                    className="text-gray-800 leading-relaxed space-y-6"
                    style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                </div>

                {/* Share Section */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Share this article</h3>
                    <p className="text-gray-600 text-sm">Help others discover this content</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleShare('twitter')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                    >
                      <Twitter size={18} />
                      <span>Twitter</span>
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Facebook size={18} />
                      <span>Facebook</span>
                    </button>
                    <button
                      onClick={() => handleShare('linkedin')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition"
                    >
                      <Linkedin size={18} />
                      <span>LinkedIn</span>
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                    >
                      <Link2 size={18} />
                      <span>Copy Link</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Right Side */}
            <div className="lg:w-80 lg:flex-shrink-0">
              {/* Popular Articles */}
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <Eye className="mr-2" size={20} />
                  Popular Articles
                </h3>
                
                {popularPosts.length > 0 ? (
                  <div className="space-y-4">
                    {popularPosts.map((popularPost, index) => (
                      <Link
                        key={popularPost.id}
                        to={`/blog/${popularPost.slug}`}
                        className="block group"
                      >
                        <div className="flex space-x-3 p-3 rounded-lg hover:bg-gray-50 transition">
                          <div className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                            <img
                              src={popularPost.featured_image}
                              alt={popularPost.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                            <div 
                              className="w-full h-full flex items-center justify-center text-white text-xs font-bold"
                              style={{ 
                                backgroundColor: popularPost.category_color || '#3B82F6',
                                display: 'none'
                              }}
                            >
                              {popularPost.title.split(' ').slice(0, 2).join(' ').slice(0, 8)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition line-clamp-2">
                              {popularPost.title}
                            </h4>
                            <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                              <Eye size={12} />
                              <span>{popularPost.views || 0}</span>
                              <span>•</span>
                              <span>{popularPost.reading_time || 5} min</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">No popular articles found</p>
                  </div>
                )}
                
                {/* Back to Blog Button */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Link
                    to="/blog"
                    className="flex items-center justify-center space-x-2 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                  >
                    <ArrowLeft size={16} />
                    <span>Back to Blog</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {post.related_posts && post.related_posts.length > 0 && (
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-6xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {post.related_posts.map((relatedPost) => (
                  <BlogCard key={relatedPost.id} post={relatedPost} />
                ))}
              </div>
            </div>
          </div>
        )}
      </article>
    </>
  );
};

export default BlogDetail;
